import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Plane, Mail, Search, ChevronDown, Sparkles, Ticket, CreditCard } from 'lucide-react';
import type { Agent, Client, Flight, QuotePassenger, QuoteRecord } from '../types';
import { ageFromDateOfBirth, approximateDateOfBirthFromAge, normalizeDateOfBirth } from '../lib/passengerBirthDate';
import {
  passengersForQuoteRecord,
  resizePassengers,
  validatePassengers,
} from '../lib/passengers';
import { buildBookingQuoteUrl, buildPaymentUrl, buildViewTicketUrl } from '../lib/hosts';
import {
  buildSkyluxTicketingQuoteMailHtml,
  formatFlightQuoteDetailsPlain,
  formatFlightQuoteSummaryPlain,
} from '../lib/skyluxClientMailLayouts';
import { buildSkyluxQuoteClientMailHtml, buildSkyluxQuoteEmailIntroPlain, buildPaymentEmailSubject, buildQuoteEmailSubject } from '../lib/skyluxQuoteMailHtml';
import { generateConfirmationNumber, generateEticketNumber } from '../lib/skyluxReceiptIds';
import { appendEmailLog, loadCms, logQuoteSent, saveQuote } from '../lib/skyluxStore';
import {
  createQuote as createQuoteApi,
  createTicket,
  fetchFlights,
  recordQuoteSend,
  searchFlights as searchFlightsApi,
  searchReturnFlights,
  sendTransactionalEmail,
} from '../lib/api';
import {
  attachDepartureTokenFromRoundTripSearch,
  enrichFlightWithReturn,
  flightsMatchForToken,
  resolveOutboundWithToken,
} from '../lib/flightItinerary';
import { mergeRoundTripFlight, roundTripLegReferences, roundTripReferencePerPerson } from '../lib/roundTrip';
import AirportAutocompleteInput from './AirportAutocompleteInput';

export type DepartureTimeFilter = 'any' | 'morning' | 'afternoon' | 'evening';

/** Search panel defaults when opening the modal from Admin → Flight Search (exact flight row). */
export type FlightQuoteSearchContext = {
  tripType: '1' | '2';
  departureId: string;
  arrivalId: string;
  outboundDate: string;
  returnDate: string;
  adults: number;
  bags: number;
  stops: '0' | '1' | '2' | '3';
  travelClass: '1' | '2' | '3' | '4';
  airlineFilter: string;
  timeBucket: DepartureTimeFilter;
  /** Rows from the Flight Search page (keeps SerpAPI departure_token when present). */
  liveFlights?: Flight[];
};

export type FlightQuoteModalProps = {
  open: boolean;
  onClose: () => void;
  agent: Agent;
  /** When null, the agent is starting a quote for a client not yet in the directory. */
  client: Client | null;
  onUpsertClient: (client: Client) => void;
  onAfterQuoteSent?: (quote: QuoteRecord) => void;
  /** Pre-select a row from the flight inventory (e.g. from Flights page or deep link). */
  preselectFlightId?: string | null;
  /** Pre-select a concrete flight object from live search. */
  preselectFlight?: Flight | null;
  /** When set (e.g. from Flight Search), pre-fills trip / airports / dates / filters to match that search. */
  initialSearchFromResults?: FlightQuoteSearchContext | null;
};

function hourInBucket(hour: number, bucket: DepartureTimeFilter): boolean {
  if (bucket === 'any') return true;
  if (bucket === 'morning') return hour >= 5 && hour < 12;
  if (bucket === 'afternoon') return hour >= 12 && hour < 17;
  return hour >= 17 || hour < 5;
}

function newDraftClient(): Client {
  const id = `c_${Date.now()}`;
  return {
    id,
    name: '',
    email: '',
    phone: '',
    requestDate: new Date().toISOString().split('T')[0],
    status: 'pending',
    origin: '',
    destination: '',
    travelDate: '',
    source: 'manual',
  };
}

function quoteEmailSubject(destination: string, pricePerPerson: number): string {
  return buildQuoteEmailSubject(destination, pricePerPerson);
}

const FlightQuoteModal: React.FC<FlightQuoteModalProps> = ({
  open,
  onClose,
  agent,
  client,
  onUpsertClient,
  onAfterQuoteSent,
  preselectFlightId = null,
  preselectFlight = null,
  initialSearchFromResults = null,
}) => {
  const [draft, setDraft] = useState<Client>(() => client ?? newDraftClient());
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [date, setDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [tripType, setTripType] = useState<'1' | '2'>('2');
  const [adults, setAdults] = useState(1);
  const [bags, setBags] = useState(0);
  const [stops, setStops] = useState<'0' | '1' | '2' | '3'>('0');
  const [travelClass, setTravelClass] = useState<'1' | '2' | '3' | '4'>('1');
  const [airline, setAirline] = useState('');
  const [timeBucket, setTimeBucket] = useState<DepartureTimeFilter>('any');
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [liveFlights, setLiveFlights] = useState<Flight[]>([]);
  const [searchingLive, setSearchingLive] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [pricePerPerson, setPricePerPerson] = useState('');
  const [passengers, setPassengers] = useState<QuotePassenger[]>([
    { name: '', age: undefined, dateOfBirth: undefined },
  ]);
  const [returnFlights, setReturnFlights] = useState<Flight[]>([]);
  const [selectedReturnFlight, setSelectedReturnFlight] = useState<Flight | null>(null);
  const [loadingReturn, setLoadingReturn] = useState(false);
  const [returnSearchError, setReturnSearchError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sentBanner, setSentBanner] = useState<string | null>(null);
  const [refineSearchOpen, setRefineSearchOpen] = useState(true);
  const navigate = useNavigate();
  const { agentId: routeAgentId } = useParams();
  const pendingPreselectRef = useRef<Flight | null>(null);
  const autoSearchRanRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const base = client ? { ...client } : newDraftClient();
    if (initialSearchFromResults) {
      const s = initialSearchFromResults;
      setDraft({
        ...base,
        origin: s.departureId,
        destination: s.arrivalId,
        travelDate: s.outboundDate,
      });
      setOrigin(s.departureId);
      setDestination(s.arrivalId);
      setDate(s.outboundDate);
      setReturnDate(s.returnDate);
      setTripType(s.tripType);
      setAdults(s.adults);
      setBags(s.bags);
      setStops(s.stops);
      setTravelClass(s.travelClass);
      setAirline(s.airlineFilter);
      setTimeBucket(s.timeBucket);
    } else {
      setDraft(base);
      setOrigin(base.origin ?? '');
      setDestination(base.destination ?? '');
      setDate(base.travelDate ?? '');
      setReturnDate('');
      setTripType('2');
      setAdults(1);
      setBags(0);
      setStops('0');
      setTravelClass('1');
      setAirline('');
      setTimeBucket('any');
    }
    setSelectedFlight(null);
    setSearchError(null);
    setSearchingLive(false);
    setPricePerPerson('');
    setSentBanner(null);
    setRefineSearchOpen(true);
    const seedName = (client ?? base).name?.trim() ?? '';
    const paxCount = initialSearchFromResults?.adults ?? 1;
    setPassengers(resizePassengers([], paxCount, seedName));
    setReturnFlights([]);
    setSelectedReturnFlight(null);
    setReturnSearchError(null);
    setLoadingReturn(false);
    const cached = initialSearchFromResults?.liveFlights ?? [];
    const ctxTrip = initialSearchFromResults?.tripType ?? '2';
    const cachedHasReturnTokens = cached.some((f) => f.departureToken?.trim());
    // Round-trip quotes need departure_token — do not reuse one-way result rows from Flight Search.
    if (ctxTrip === '1' && cached.length > 0 && !cachedHasReturnTokens) {
      setLiveFlights([]);
    } else {
      setLiveFlights(cached);
    }
    autoSearchRanRef.current = false;
    pendingPreselectRef.current = null;
  }, [open, client, initialSearchFromResults]);

  useEffect(() => {
    if (!open) return;
    if (preselectFlight) {
      pendingPreselectRef.current = preselectFlight;
      if (preselectFlight.departureToken?.trim()) {
        setSelectedFlight(preselectFlight);
        setPricePerPerson(String(preselectFlight.price));
        setRefineSearchOpen(false);
        pendingPreselectRef.current = null;
      }
      return;
    }
    if (!preselectFlightId) return;
    void (async () => {
      try {
        const rows = await fetchFlights();
        const f = rows.find((x) => x.id === preselectFlightId);
        if (f) {
          setSelectedFlight(f);
          setPricePerPerson(String(f.price));
          setRefineSearchOpen(false);
        }
      } catch {
        // no-op
      }
    })();
  }, [open, preselectFlightId, preselectFlight]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const showTripForm = refineSearchOpen || !selectedFlight;

  const visibleFlights = useMemo(() => {
    const al = airline.trim().toLowerCase();
    return liveFlights.filter((f) => {
      if (al && !f.airline.toLowerCase().includes(al)) return false;
      if (!hourInBucket(f.departureHour, timeBucket)) return false;
      return true;
    });
  }, [liveFlights, airline, timeBucket]);

  const resolveRouteForSearch = () => {
    const dep = (origin || draft.origin || '').trim().toUpperCase();
    const arr = (destination || draft.destination || '').trim().toUpperCase();
    const outDate = (date || draft.travelDate || '').trim();
    return { dep, arr, outDate };
  };

  const loadReturnOptions = async (outbound: Flight) => {
    setSelectedReturnFlight(null);
    setReturnFlights([]);
    setReturnSearchError(null);
    if (tripType !== '1' || !returnDate.trim()) {
      return;
    }

    setLoadingReturn(true);
    try {
      let ob = resolveOutboundWithToken(outbound, liveFlights);
      if (ob.departureToken && ob !== outbound) {
        setSelectedFlight(ob);
      }
      if (!ob.departureToken?.trim()) {
        const { dep, arr, outDate } = resolveRouteForSearch();
        if (dep && arr && outDate) {
          ob = await attachDepartureTokenFromRoundTripSearch(ob, {
            departureId: dep,
            arrivalId: arr,
            outboundDate: outDate,
            returnDate: returnDate.trim(),
            adults,
            bags,
            stops,
            travelClass,
          });
          if (ob.departureToken && ob !== outbound) {
            setSelectedFlight(ob);
          }
        }
      }

      if (!ob.departureToken?.trim()) {
        setReturnSearchError(
          searchError ||
            'No return pairing for this outbound. In step 2: Round trip, return date, Search live — then select outbound and return. Fix any airport resolution warnings first.',
        );
        return;
      }

      const route = resolveRouteForSearch();
      const departureId = route.dep || ob.departure;
      const arrivalId = route.arr || ob.arrival;
      const outboundDate = route.outDate || ob.travelDate || '';
      if (!departureId || !arrivalId || !outboundDate) {
        setReturnSearchError('Enter departure, arrival, and outbound date in step 2 before loading return flights.');
        return;
      }
      const result = await searchReturnFlights({
        departureToken: ob.departureToken,
        departureId,
        arrivalId,
        returnDate: returnDate.trim(),
        outboundDate,
        type: '1',
        adults,
        bags,
        stops,
        travelClass,
      });
      const flights = result.flights ?? [];
      setReturnFlights(flights);
      if (flights.length > 0) {
        applyReturnSelection(ob, flights[0]!);
      }
      if (flights.length === 0) {
        setReturnSearchError('No return flights found for this outbound option. Try another outbound or adjust the return date.');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Could not load return flights.';
      const needsApiRestart = /missing [`']?departure_id/i.test(msg);
      setReturnSearchError(
        needsApiRestart
          ? `${msg} Restart the API server (npm run server, or docker compose up --build -d api) so return flights use the latest code.`
          : msg,
      );
      setReturnFlights([]);
    } finally {
      setLoadingReturn(false);
    }
  };

  useEffect(() => {
    if (!open || !selectedFlight || tripType !== '1' || !returnDate.trim()) return;
    const ob = resolveOutboundWithToken(selectedFlight, liveFlights);
    if (!ob.departureToken?.trim() && liveFlights.length > 0 && !liveFlights.some((f) => f.departureToken?.trim())) {
      return;
    }
    if (ob.departureToken && ob !== selectedFlight) {
      setSelectedFlight(ob);
    }
    void loadReturnOptions(ob);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when outbound or return date changes
  }, [open, selectedFlight?.id, tripType, returnDate, liveFlights]);

  const handleSelectFlight = (f: Flight) => {
    const ob = tripType === '1' ? resolveOutboundWithToken(f, liveFlights) : f;
    setSelectedFlight(ob);
    setPricePerPerson(String(ob.price));
  };

  const applyReturnSelection = (outbound: Flight, returnFlight: Flight) => {
    setSelectedReturnFlight(returnFlight);
    setPricePerPerson(String(roundTripReferencePerPerson(outbound, returnFlight)));
  };

  const handleLiveSearch = useCallback(async () => {
    const dep = (origin || draft.origin || '').trim().toUpperCase();
    const arr = (destination || draft.destination || '').trim().toUpperCase();
    const outDate = (date || draft.travelDate || '').trim();
    if (!dep || !arr || !outDate) {
      setSearchError('Enter departure code, arrival code, and date for live search.');
      return;
    }
    if (tripType === '1' && !returnDate.trim()) {
      setSearchError('For round trip, add a return date.');
      return;
    }
    setSearchingLive(true);
    setSearchError(null);
    setReturnSearchError(null);
    try {
      const result = await searchFlightsApi({
        departureId: dep,
        arrivalId: arr,
        outboundDate: outDate,
        returnDate: tripType === '1' ? returnDate.trim() : undefined,
        type: tripType,
        adults,
        bags,
        stops,
        travelClass,
        deepSearch: true,
      });
      const flights = result.flights || [];
      setLiveFlights(flights);

      const pending = pendingPreselectRef.current;
      if (pending) {
        const matched =
          flights.find((f) => f.id === pending.id && f.departureToken) ||
          flights.find((f) => f.departureToken && flightsMatchForToken(f, pending));
        if (matched) {
          setSelectedFlight(matched);
          setPricePerPerson(String(matched.price));
          setRefineSearchOpen(false);
          pendingPreselectRef.current = null;
        } else {
          setSelectedFlight(null);
          setPricePerPerson('');
        }
      } else {
        setSelectedFlight(null);
        setSelectedReturnFlight(null);
        setReturnFlights([]);
        setPricePerPerson('');
      }

      if (tripType === '1') {
        const tokenCount =
          typeof result.departureTokensCount === 'number'
            ? result.departureTokensCount
            : flights.filter((f) => f.departureToken).length;
        if (flights.length > 0 && tokenCount === 0) {
          setSearchError(
            'SerpAPI returned no return pairing for this round trip. Confirm SERPAPI_KEY in server/.env, then try Search live again.',
          );
        }
      }
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'Live search failed');
      setLiveFlights([]);
    } finally {
      setSearchingLive(false);
    }
  }, [
    origin,
    draft.origin,
    destination,
    draft.destination,
    date,
    draft.travelDate,
    tripType,
    returnDate,
    adults,
    bags,
    stops,
    travelClass,
  ]);

  useEffect(() => {
    if (!open) {
      autoSearchRanRef.current = false;
      return;
    }
    if (autoSearchRanRef.current) return;
    if (tripType !== '1' || !returnDate.trim()) return;
    const dep = (origin || draft.origin || '').trim();
    const arr = (destination || draft.destination || '').trim();
    const outDate = (date || draft.travelDate || '').trim();
    if (!dep || !arr || !outDate) return;
    const needsFresh =
      liveFlights.length === 0 || !liveFlights.some((f) => f.departureToken?.trim());
    if (!needsFresh) return;
    autoSearchRanRef.current = true;
    void handleLiveSearch();
  }, [open, tripType, returnDate, origin, destination, date, draft.origin, draft.travelDate, liveFlights, handleLiveSearch]);

  const handleSend = async (kind: 'quote' | 'oaf' | 'ticket') => {
    if (kind === 'ticket' || kind === 'oaf') {
      const paxErr = validatePassengers(passengers);
      if (paxErr) {
        window.alert(paxErr);
        return;
      }
      if (kind === 'oaf' && adults !== passengers.length) {
        window.alert('Set Adults in trip search to match the number of travelers before sending the payment link.');
        return;
      }
    }
    const email = draft.email.trim();
    if (!email) {
      window.alert('Add or edit the client email before sending — the quote is delivered by email.');
      return;
    }
    if (!selectedFlight) return;
    const p = Number(pricePerPerson);
    if (!Number.isFinite(p) || p <= 0) {
      window.alert('Enter a valid price per person.');
      return;
    }

    setSending(true);
    try {
      let flightForQuote = selectedFlight;
      if (tripType === '1' && returnDate.trim()) {
        const returnPick = selectedReturnFlight ?? returnFlights[0] ?? null;
        if (returnPick) {
          flightForQuote = mergeRoundTripFlight(selectedFlight, returnPick, returnDate.trim());
        } else {
          let ob = selectedFlight;
          if (!ob.departureToken?.trim()) {
            const { dep, arr, outDate } = resolveRouteForSearch();
            if (dep && arr && outDate) {
              ob = await attachDepartureTokenFromRoundTripSearch(ob, {
                departureId: dep,
                arrivalId: arr,
                outboundDate: outDate,
                returnDate: returnDate.trim(),
                adults,
                bags,
                stops,
                travelClass,
              });
            }
          }
          const route = resolveRouteForSearch();
          flightForQuote = await enrichFlightWithReturn(ob, {
            tripType: '1',
            returnDate: returnDate.trim(),
            departureId: route.dep || ob.departure,
            arrivalId: route.arr || ob.arrival,
            outboundDate: route.outDate || ob.travelDate || date || draft.travelDate || '',
            adults,
            bags,
            stops,
            travelClass,
          });
          if (!flightForQuote.returnLeg) {
            window.alert(
              selectedFlight.departureToken
                ? 'Could not load return flight details. Select an outbound with return options, or try searching again.'
                : 'Return flight details are missing. Run a new round-trip search, then select outbound and return flights.',
            );
            return;
          }
        }
      }

      const cms = loadCms();
      const quoteId = `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const confirmationNumber = generateConfirmationNumber();
      const bookingUrl = buildBookingQuoteUrl(quoteId);
      const paymentUrl = buildPaymentUrl(quoteId);

      const travelRef = date || draft.travelDate || flightForQuote.travelDate;
      const contact = { email, phone: draft.phone };
      const { passengers: paxBase, clientSnapshot } =
        kind === 'quote'
          ? {
              passengers: [] as QuotePassenger[],
              clientSnapshot: {
                name: draft.name.trim() || 'Traveler',
                email: contact.email,
                phone: contact.phone,
              },
            }
          : passengersForQuoteRecord(passengers, contact, { referenceTravelDate: travelRef });
      const paxList =
        kind === 'quote'
          ? []
          : kind === 'oaf'
            ? paxBase
            : paxBase.map((p) => ({ ...p, eTicketNumber: generateEticketNumber() }));
      const eTicketNumber =
        kind === 'ticket' ? (paxList[0]?.eTicketNumber ?? generateEticketNumber()) : undefined;

      const resolvedClient: Client = {
        ...draft,
        name: clientSnapshot.name,
        email: clientSnapshot.email,
        phone: clientSnapshot.phone,
        origin: origin || draft.origin,
        destination: destination || draft.destination,
        travelDate: date || draft.travelDate,
        status: 'quoted',
      };

      onUpsertClient(resolvedClient);

      let record: QuoteRecord = {
        id: quoteId,
        clientId: resolvedClient.id,
        agentId: agent.id,
        flight: flightForQuote,
        pricePerPerson: p,
        passengers: paxList.length > 0 ? paxList : undefined,
        bags,
        adults,
        clientSnapshot,
        createdAt: new Date().toISOString(),
        bookingUrl,
        paymentUrl,
        paymentStatus: 'unpaid',
        confirmationNumber,
        eTicketNumber,
      };
      saveQuote(record);
      void createQuoteApi(record);

      let createdTicket: Awaited<ReturnType<typeof createTicket>>;
      try {
        createdTicket = await createTicket({
          title: flightForQuote.returnLeg
            ? `Quote follow-up: ${flightForQuote.departure} -> ${flightForQuote.arrival} (rtn ${flightForQuote.returnLeg.arrival})`
            : `Quote follow-up: ${flightForQuote.departure} -> ${flightForQuote.arrival}`,
          description: [
            `Auto-created from quote ${quoteId}.`,
            `Confirmation number: ${confirmationNumber}`,
            `E-ticket number: ${eTicketNumber}`,
            '',
            formatFlightQuoteDetailsPlain(flightForQuote, p, paxList),
            '',
            `Booking link: ${bookingUrl}`,
            `Payment link: ${paymentUrl}`,
          ].join('\n'),
          clientName: resolvedClient.name,
          clientEmail: resolvedClient.email,
          priority: 'medium',
          assignee: agent.id,
          tags: [
            'quote',
            flightForQuote.airline.toLowerCase(),
            flightForQuote.departure.toLowerCase(),
            flightForQuote.arrival.toLowerCase(),
            ...(flightForQuote.returnLeg ? [flightForQuote.returnLeg.arrival.toLowerCase()] : []),
          ],
        });
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Could not create support ticket for this quote.');
        return;
      }

      void recordQuoteSend({
        client: JSON.parse(JSON.stringify(resolvedClient)) as Record<string, unknown>,
        quote: JSON.parse(JSON.stringify(record)) as Record<string, unknown>,
        ticket: JSON.parse(JSON.stringify(createdTicket)) as Record<string, unknown>,
        flight: JSON.parse(JSON.stringify(flightForQuote)) as Record<string, unknown>,
        emailKind: kind,
      }).catch(() => {});

      const routeLabel = flightForQuote.returnLeg
        ? `${flightForQuote.departure} → ${flightForQuote.arrival} · return → ${flightForQuote.returnLeg.arrival}`
        : `${flightForQuote.departure} → ${flightForQuote.arrival}`;
      logQuoteSent({
        agentId: agent.id,
        clientId: resolvedClient.id,
        clientName: resolvedClient.name,
        flightLabel: `${flightForQuote.airline} ${flightForQuote.flightNumber} (${routeLabel})`,
        pricePerPerson: p,
      });

      const subject =
        kind === 'quote'
          ? quoteEmailSubject(flightForQuote.arrival, p)
          : kind === 'oaf'
            ? buildPaymentEmailSubject(flightForQuote.arrival, p)
            : `SkyLux Travel Ticket Receipt. Confirmation number: ${confirmationNumber}`;
      const flightSummaryPlain = formatFlightQuoteSummaryPlain(flightForQuote, p);
      const passengerCount = kind === 'quote' ? adults : paxList.length;
      const totalUsd = p * passengerCount;
      const quoteMailAgentForIntro = {
        name: agent.name,
        nickname: agent.nickname,
      };
      const previewLines =
        kind === 'quote'
          ? [
              buildSkyluxQuoteEmailIntroPlain({
                clientName: clientSnapshot.name,
                agent: quoteMailAgentForIntro,
                flight: flightForQuote,
                primaryCta: 'booking',
              }),
              '',
              `Total: ${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`,
              `Review & book (enter passenger details): ${bookingUrl}`,
            ]
          : kind === 'oaf'
            ? [
                buildSkyluxQuoteEmailIntroPlain({
                  clientName: clientSnapshot.name,
                  agent: quoteMailAgentForIntro,
                  flight: flightForQuote,
                  primaryCta: 'payment',
                }),
                '',
                ...paxList.map((row, i) => `${i + 1}. ${row.name}${row.age != null ? ` (age ${row.age})` : ''}`),
                '',
                `Total: ${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`,
                `Complete payment (OAF checkout): ${paymentUrl}`,
              ]
            : [
              cms.quoteEmailIntro,
              '',
              `SkyLux Travel Ticket Receipt. Confirmation number: ${confirmationNumber}`,
              `Ticket number: ${eTicketNumber}`,
              `Quote reference: ${quoteId}`,
              `Support ticket: ${createdTicket.code}`,
              '',
              flightSummaryPlain,
              '',
              `View ticket: ${eTicketNumber ? buildViewTicketUrl(eTicketNumber) : bookingUrl}`,
              `View invoice: ${buildPaymentUrl(quoteId, { view: 'invoice' })}`,
            ];
      const preview = previewLines.join('\n');

      setSentBanner(null);

      let mailFooter = '';
      let fromAddr = agent.email;
      const baseMailParams = {
        clientName: clientSnapshot.name,
        introText: cms.quoteEmailIntro,
        flight: flightForQuote,
        pricePerPerson: p,
        passengers: paxList,
        bookingUrl,
        paymentUrl,
        confirmationNumber,
        eTicketNumber: eTicketNumber ?? '—',
        supportTicketCode: createdTicket.code,
        quoteId,
        signatureName: agent.name,
        signatureEmail: agent.email,
        ...(eTicketNumber ? { primaryCtaUrl: buildViewTicketUrl(eTicketNumber) } : {}),
      };
      const htmlBodyStandard = buildSkyluxTicketingQuoteMailHtml(baseMailParams);
      const quoteMailAgent = {
        name: agent.name,
        nickname: agent.nickname,
        email: agent.email,
        avatar: agent.avatar,
      };
      const mailSequence: { html: string; logNote: string; logKind: 'quote' | 'oaf' | 'ticket_receipt' }[] =
        kind === 'quote'
          ? [
              {
                html: buildSkyluxQuoteClientMailHtml({
                  clientName: clientSnapshot.name,
                  introText: cms.quoteEmailIntro,
                  flight: flightForQuote,
                  pricePerPerson: p,
                  passengerCount,
                  bookingUrl,
                  paymentUrl,
                  quoteId,
                  agent: quoteMailAgent,
                  primaryCta: 'booking',
                }),
                logNote: 'Quote email: Review & book (passenger details on booking)',
                logKind: 'quote',
              },
            ]
          : kind === 'oaf'
            ? [
                {
                  html: buildSkyluxQuoteClientMailHtml({
                    clientName: clientSnapshot.name,
                    introText: cms.quoteEmailIntro,
                    flight: flightForQuote,
                    pricePerPerson: p,
                    passengerCount,
                    bookingUrl,
                    paymentUrl,
                    quoteId,
                    agent: quoteMailAgent,
                    passengers: paxList,
                    primaryCta: 'payment',
                  }),
                  logNote: 'OAF email: Complete payment (passengers on checkout)',
                  logKind: 'oaf',
                },
              ]
            : [{ html: htmlBodyStandard, logNote: 'Ticket email: View ticket + View invoice', logKind: 'ticket_receipt' }];
      const sendLines: string[] = [];
      const logId = () => `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

      try {
        for (let i = 0; i < mailSequence.length; i += 1) {
          const step = mailSequence[i]!;
          const mail = await sendTransactionalEmail({ to: email, subject, text: preview, html: step.html });
          fromAddr = mail.from;
          sendLines.push(
            `Email ${i + 1} of ${mailSequence.length}: ${step.logNote} · ${mail.from}${mail.messageId ? ` · ${mail.messageId}` : ''}`,
          );
          appendEmailLog({
            id: logId(),
            at: new Date().toISOString(),
            agentId: agent.id,
            agentEmail: mail.from,
            to: email,
            subject,
            kind: step.logKind,
            preview: `${preview}\n\n— ${step.logNote}`,
            deliveryStatus: 'sent',
            quoteId,
          });
        }
        mailFooter = `\n\n— Sent ${mailSequence.length} email(s).\n${sendLines.join('\n')}`;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        const failKind = mailSequence[sendLines.length]?.logKind ?? (kind === 'quote' ? 'quote' : kind === 'oaf' ? 'oaf' : 'ticket_receipt');
        if (sendLines.length === 0) {
          mailFooter = `\n\n— Email not delivered: ${msg}`;
          appendEmailLog({
            id: logId(),
            at: new Date().toISOString(),
            agentId: agent.id,
            agentEmail: fromAddr,
            to: email,
            subject,
            kind: failKind,
            preview: preview + mailFooter,
            deliveryStatus: 'failed',
            errorMessage: msg,
            quoteId,
          });
        } else {
          mailFooter = `\n\n— ${sendLines.join('\n')}\n— Next email failed: ${msg}`;
          appendEmailLog({
            id: logId(),
            at: new Date().toISOString(),
            agentId: agent.id,
            agentEmail: fromAddr,
            to: email,
            subject,
            kind: failKind,
            preview: `${preview}\n\n— FAILED before all variants were sent: ${msg}`,
            deliveryStatus: 'partial',
            errorMessage: msg,
            quoteId,
          });
        }
      }

      const ticketLine = ` Ticket ${createdTicket.code} is open in Ticket Desk.`;
      const allSent = sendLines.length === mailSequence.length;
      if (allSent) {
        const sentAt = new Date().toISOString();
        const emailsSent = { ...record.emailsSent };
        if (kind === 'quote') emailsSent.quote = sentAt;
        else if (kind === 'oaf') emailsSent.oaf = sentAt;
        else emailsSent.ticket = sentAt;
        record = { ...record, emailsSent };
        saveQuote(record);
        void createQuoteApi(record);
      }
      const kindLabel =
        kind === 'quote'
          ? 'Quote email (booking link — client completes details there)'
          : kind === 'oaf'
            ? 'OAF payment email (checkout with passengers on file)'
            : 'Ticket email (standard layout)';
      setSentBanner(
        allSent
          ? `Quote saved and ${kindLabel} sent to ${email} from ${fromAddr}.${ticketLine}`
          : sendLines.length > 0 && sendLines.length < mailSequence.length
            ? `Quote saved; ${sendLines.length} of ${mailSequence.length} email(s) sent.${ticketLine}${mailFooter}`
            : `Quote saved. ${mailFooter.replace(/^\n\n— /, '')}${ticketLine}`,
      );
      onAfterQuoteSent?.(record);

      const agentRoute = routeAgentId ?? agent.id;
      try {
        sessionStorage.setItem('skylux_focus_ticket_id', createdTicket.id);
      } catch {
        // no-op
      }
      navigate(`/admin/agent/${agentRoute}/tickets?ticket=${encodeURIComponent(createdTicket.id)}`, { replace: false });
      onClose();
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'clamp(0.75rem, 3vw, 2rem)',
      }}
    >
      <div
        className="animate-fade"
        style={{
          background: 'white',
          width: '100%',
          maxWidth: '1040px',
          borderRadius: '1.35rem',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'min(92vh, 900px)',
          color: 'var(--text-main)',
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
        }}
      >
        <div
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem',
            flexWrap: 'wrap',
            background: 'linear-gradient(180deg, #fafbfc 0%, #fff 100%)',
            borderRadius: '1.35rem 1.35rem 0 0',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
              <h3 style={{ fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>Quote workspace</h3>
              {selectedFlight ? (
                <span
                  style={{
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '6px',
                    background: '#dbeafe',
                    color: '#1d4ed8',
                  }}
                >
                  Flight selected
                </span>
              ) : (
                <span
                  style={{
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '6px',
                    background: '#f1f5f9',
                    color: 'var(--text-muted)',
                  }}
                >
                  Pick a flight
                </span>
              )}
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', margin: 0, lineHeight: 1.5, maxWidth: '62ch' }}>
              Confirm client details, run a live search if needed, then set price and send. Emails are sent from the server{' '}
              <strong>MAIL_FROM</strong> address (e.g. jordan@xn--syluxtravel-e2b.com). Your name (<strong>{agent.nickname}</strong>) is used in the signature only.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: '#f1f5f9',
              borderRadius: '12px',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', flex: 1 }}>
          <div
            style={{
              marginBottom: '1.25rem',
              padding: '1.15rem 1.25rem',
              background: '#f8fafc',
              borderRadius: '1rem',
              border: '1px solid var(--border)',
            }}
          >
            <p
              style={{
                fontSize: '0.72rem',
                fontWeight: 800,
                color: 'var(--text-muted)',
                marginBottom: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              1 · Contact & passengers
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Passengers ({passengers.length}) — name, age, and date of birth for each adult
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                {passengers.map((p, i) => (
                  <div key={`pax-field-${i}`}>
                    <label
                      style={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: 'var(--text-muted)',
                        display: 'block',
                        marginBottom: '0.3rem',
                      }}
                    >
                      Adult {i + 1}
                    </label>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr minmax(72px, 88px) minmax(140px, 1fr)',
                        gap: '0.5rem',
                      }}
                    >
                      <input
                        value={p.name}
                        onChange={(e) =>
                          setPassengers((list) =>
                            list.map((row, j) => (j === i ? { ...row, name: e.target.value } : row)),
                          )
                        }
                        placeholder="Full name (as on passport)"
                      />
                      <input
                        type="number"
                        min={1}
                        max={120}
                        value={p.age ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const age = raw === '' ? undefined : Math.max(1, Math.min(120, Number(raw) || 0));
                          const travelRef = date || draft.travelDate;
                          setPassengers((list) =>
                            list.map((row, j) => {
                              if (j !== i) return row;
                              const nextAge = age || undefined;
                              const keepDob = normalizeDateOfBirth(row.dateOfBirth);
                              const nextDob =
                                nextAge != null && !keepDob
                                  ? approximateDateOfBirthFromAge(nextAge, travelRef)
                                  : keepDob ?? row.dateOfBirth;
                              return { ...row, age: nextAge, dateOfBirth: nextDob };
                            }),
                          );
                        }}
                        placeholder="Age"
                        aria-label={`Age for adult ${i + 1}`}
                      />
                      <input
                        type="date"
                        value={p.dateOfBirth ?? ''}
                        onChange={(e) => {
                          const dob = normalizeDateOfBirth(e.target.value) ?? undefined;
                          const travelRef = date || draft.travelDate;
                          setPassengers((list) =>
                            list.map((row, j) => {
                              if (j !== i) return row;
                              // When DOB is entered, auto-calculate age using the quote travel date.
                              const nextAge = dob ? ageFromDateOfBirth(dob, travelRef) : row.age;
                              return { ...row, dateOfBirth: dob, age: nextAge };
                            }),
                          );
                        }}
                        aria-label={`Date of birth for adult ${i + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {adults !== passengers.length && (
                <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Adjust <strong>Adults</strong> in trip search to match the number of travelers.
                </p>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '0.35rem' }}>
                  Contact email (all passengers)
                </label>
                <input
                  type="email"
                  value={draft.email}
                  onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                  placeholder="name@example.com"
                />
              </div>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: '0.35rem' }}>Phone</label>
                <input value={draft.phone ?? ''} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} placeholder="+1 …" />
              </div>
            </div>
            {!draft.email.trim() && (
              <p style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: '#b45309', fontWeight: 600 }}>
                Add an email so the quote can be delivered to the client.
              </p>
            )}
          </div>

          {initialSearchFromResults && selectedFlight && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.75rem 1rem',
                borderRadius: '0.75rem',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                fontSize: '0.85rem',
                color: '#1e40af',
                lineHeight: 1.5,
              }}
            >
              This flight was opened from your search results. Review price and client, then send — or expand <strong>Trip & live search</strong> to pick a different option.
            </div>
          )}

          {selectedFlight && !showTripForm && (
            <button
              type="button"
              onClick={() => setRefineSearchOpen(true)}
              style={{
                width: '100%',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.65rem 1rem',
                borderRadius: '0.75rem',
                border: '1px dashed var(--accent)',
                background: '#fff',
                color: 'var(--accent)',
                fontWeight: 700,
                fontSize: '0.88rem',
                cursor: 'pointer',
              }}
            >
              <ChevronDown size={18} aria-hidden />
              Trip & live search (change flight)
            </button>
          )}

          {showTripForm && (
            <>
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '1rem 1.15rem',
                  background: '#fafbfc',
                  borderRadius: '1rem',
                  border: '1px solid var(--border)',
                }}
              >
                <p
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    color: 'var(--text-muted)',
                    marginBottom: '0.85rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  2 · Trip & live search
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1rem', maxWidth: '380px', padding: '0.25rem', background: '#e2e8f0', borderRadius: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setTripType('2');
                      setReturnFlights([]);
                      setSelectedReturnFlight(null);
                      setReturnSearchError(null);
                      setSearchError(null);
                    }}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      padding: '0.55rem 0.85rem',
                      borderRadius: '0.6rem',
                      border: 'none',
                      fontWeight: 800,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      background: tripType === '2' ? 'white' : 'transparent',
                      color: tripType === '2' ? 'var(--accent)' : 'var(--text-muted)',
                      boxShadow: tripType === '2' ? '0 1px 4px rgba(15,23,42,0.08)' : 'none',
                    }}
                  >
                    One way
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTripType('1');
                      if (liveFlights.length > 0 && !liveFlights.some((f) => f.departureToken)) {
                        setLiveFlights([]);
                        setSelectedFlight(null);
                        setSelectedReturnFlight(null);
                        setReturnFlights([]);
                        setSearchError('Round trip needs a new search. Set return date, then click Search live.');
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      padding: '0.55rem 0.85rem',
                      borderRadius: '0.6rem',
                      border: 'none',
                      fontWeight: 800,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      background: tripType === '1' ? 'white' : 'transparent',
                      color: tripType === '1' ? 'var(--accent)' : 'var(--text-muted)',
                      boxShadow: tripType === '1' ? '0 1px 4px rgba(15,23,42,0.08)' : 'none',
                    }}
                  >
                    Round trip
                  </button>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                    gap: '0.85rem',
                  }}
                >
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>From</label>
                    <div style={{ marginTop: '0.35rem' }}>
                      <AirportAutocompleteInput value={origin} onChange={setOrigin} placeholder="e.g. JFK" showResolved />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>To</label>
                    <div style={{ marginTop: '0.35rem' }}>
                      <AirportAutocompleteInput value={destination} onChange={setDestination} placeholder="e.g. LHR" showResolved />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Depart</label>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ marginTop: '0.35rem' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Return</label>
                    <input
                      type="date"
                      value={returnDate}
                      onChange={(e) => {
                        setReturnDate(e.target.value);
                        if (selectedFlight) void loadReturnOptions(selectedFlight);
                      }}
                      disabled={tripType !== '1'}
                      style={{ marginTop: '0.35rem', opacity: tripType === '1' ? 1 : 0.5 }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Adults</label>
                    <input
                      type="number"
                      min={1}
                      max={9}
                      value={adults}
                      onChange={(e) => {
                        const n = Math.max(1, Math.min(9, Number(e.target.value) || 1));
                        setAdults(n);
                        setPassengers((prev) => resizePassengers(prev, n));
                      }}
                      style={{ marginTop: '0.35rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bags</label>
                    <input type="number" min={0} max={9} value={bags} onChange={(e) => setBags(Math.max(0, Math.min(9, Number(e.target.value) || 0)))} style={{ marginTop: '0.35rem' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stops</label>
                    <select value={stops} onChange={(e) => setStops(e.target.value as '0' | '1' | '2' | '3')} style={{ marginTop: '0.35rem' }}>
                      <option value="0">Any</option>
                      <option value="1">Nonstop only</option>
                      <option value="2">≤ 1 stop</option>
                      <option value="3">≤ 2 stops</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cabin</label>
                    <select value={travelClass} onChange={(e) => setTravelClass(e.target.value as '1' | '2' | '3' | '4')} style={{ marginTop: '0.35rem' }}>
                      <option value="1">Economy</option>
                      <option value="2">Premium economy</option>
                      <option value="3">Business</option>
                      <option value="4">First</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Airline filter</label>
                    <input value={airline} onChange={(e) => setAirline(e.target.value)} placeholder="Narrow results" style={{ marginTop: '0.35rem' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Departure time</label>
                    <select value={timeBucket} onChange={(e) => setTimeBucket(e.target.value as DepartureTimeFilter)} style={{ marginTop: '0.35rem' }}>
                      <option value="any">Any</option>
                      <option value="morning">Morning (05–12)</option>
                      <option value="afternoon">Afternoon (12–17)</option>
                      <option value="evening">Evening (17–05)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => void handleLiveSearch()}
                  disabled={searchingLive}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.45rem',
                    background: 'var(--accent)',
                    color: 'white',
                    padding: '0.65rem 1.15rem',
                    borderRadius: '0.75rem',
                    fontWeight: 800,
                    border: 'none',
                    cursor: searchingLive ? 'wait' : 'pointer',
                    opacity: searchingLive ? 0.85 : 1,
                  }}
                >
                  <Sparkles size={18} aria-hidden />
                  {searchingLive ? 'Searching…' : 'Search live (SerpApi)'}
                </button>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', maxWidth: '48ch', lineHeight: 1.45 }}>
                  From/To are resolved to airport codes automatically. Results appear below; tap a row to select.
                </span>
              </div>
              {searchError && (
                <p role="alert" style={{ color: '#dc2626', marginTop: 0, marginBottom: '0.75rem', fontSize: '0.88rem' }}>
                  {searchError}
                </p>
              )}
            </>
          )}

          <p
            style={{
              fontSize: '0.72rem',
              fontWeight: 800,
              color: 'var(--text-muted)',
              marginBottom: '0.65rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            3 · Choose fare
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            <Search size={16} aria-hidden />
            {visibleFlights.length} option{visibleFlights.length === 1 ? '' : 's'} {liveFlights.length > 0 ? 'from live search' : '— run search in step 2'}
          </div>

          <div style={{ display: 'grid', gap: '0.65rem' }}>
            {visibleFlights.length === 0 && selectedFlight ? (
              <div
                style={{
                  padding: '1.1rem 1.25rem',
                  borderRadius: '0.9rem',
                  border: '2px solid var(--accent)',
                  background: '#eff6ff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                  <Plane size={22} color="var(--accent)" aria-hidden />
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '0.88rem' }}>Itinerary from your search</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                      {selectedFlight.airline} {selectedFlight.flightNumber} · {selectedFlight.departure} → {selectedFlight.arrival}
                    </div>
                  </div>
                </div>
                <div style={{ fontWeight: 900, fontSize: '1.2rem', color: 'var(--primary)' }}>${selectedFlight.price}</div>
              </div>
            ) : null}
            {visibleFlights.length === 0 && !selectedFlight ? (
              <div
                style={{
                  padding: '2rem 1.25rem',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  border: '1px dashed var(--border)',
                  borderRadius: '1rem',
                  background: '#fafafa',
                  fontSize: '0.9rem',
                  lineHeight: 1.55,
                }}
              >
                {liveFlights.length === 0 ? (
                  <>
                    <strong style={{ color: 'var(--text-main)' }}>No live results yet.</strong>
                    <br />
                    Enter route and date in step 2, then use <strong>Search live</strong>.
                  </>
                ) : (
                  <>
                    <strong style={{ color: 'var(--text-main)' }}>Nothing matches your filters.</strong>
                    <br />
                    Widen airline or departure-time filters, then search again.
                  </>
                )}
              </div>
            ) : null}
            {visibleFlights.length > 0
              ? visibleFlights.map((flight) => (
                <button
                  type="button"
                  key={flight.id}
                  onClick={() => handleSelectFlight(flight)}
                  style={{
                    textAlign: 'left',
                    padding: '1.1rem 1.25rem',
                    borderRadius: '0.9rem',
                    border: '2px solid',
                    borderColor: selectedFlight?.id === flight.id ? 'var(--accent)' : '#e2e8f0',
                    background: selectedFlight?.id === flight.id ? '#eff6ff' : 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0 }}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: selectedFlight?.id === flight.id ? 'white' : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Plane size={22} color="var(--accent)" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: 800, margin: 0 }}>{flight.airline}</p>
                      <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>
                        {flight.flightNumber} · {flight.departure} → {flight.arrival}
                        {flight.travelDate ? ` · ${flight.travelDate}` : ''}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexShrink: 0 }}>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontWeight: 800, fontSize: '1.05rem', margin: 0 }}>
                        {flight.departureTime} – {flight.arrivalTime}
                      </p>
                      <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0.15rem 0 0' }}>Local times</p>
                    </div>
                    <p style={{ fontWeight: 900, fontSize: '1.25rem', color: 'var(--primary)', margin: 0 }}>${flight.price}</p>
                  </div>
                </button>
              ))
              : null}
          </div>

          {selectedFlight && tripType === '1' && returnDate.trim() && (
            <>
              <p
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  color: 'var(--text-muted)',
                  margin: '1.25rem 0 0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                3b · Choose return flight
              </p>
              {loadingReturn && (
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                  {selectedFlight.departureToken ? 'Loading return flights…' : 'Linking outbound to return options…'}
                </p>
              )}
              {returnSearchError && !loadingReturn && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <p role="alert" style={{ fontSize: '0.85rem', color: '#dc2626', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                    {returnSearchError}
                  </p>
                  <button
                    type="button"
                    onClick={() => void loadReturnOptions(selectedFlight)}
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      padding: '0.45rem 0.85rem',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--accent)',
                      background: '#eff6ff',
                      color: 'var(--accent)',
                      cursor: 'pointer',
                    }}
                  >
                    Load return flights
                  </button>
                </div>
              )}
              {selectedReturnFlight && (
                <div
                  style={{
                    marginBottom: '0.85rem',
                    padding: '0.85rem 1rem',
                    borderRadius: '0.85rem',
                    border: '2px solid #86efac',
                    background: '#ecfdf5',
                    fontSize: '0.82rem',
                  }}
                >
                  <strong>Return selected:</strong> {selectedReturnFlight.airline} {selectedReturnFlight.flightNumber} ·{' '}
                  {selectedReturnFlight.departure} → {selectedReturnFlight.arrival} · {selectedReturnFlight.departureTime}–
                  {selectedReturnFlight.arrivalTime}
                </div>
              )}
              {returnFlights.length > 0 && (
                <div style={{ display: 'grid', gap: '0.65rem', marginBottom: '0.5rem' }}>
                  {returnFlights.map((flight) => (
                    <button
                      type="button"
                      key={`return-${flight.id}`}
                      onClick={() => selectedFlight && applyReturnSelection(selectedFlight, flight)}
                      style={{
                        textAlign: 'left',
                        padding: '1rem 1.15rem',
                        borderRadius: '0.9rem',
                        border: '2px solid',
                        borderColor: selectedReturnFlight?.id === flight.id ? '#16a34a' : '#e2e8f0',
                        background: selectedReturnFlight?.id === flight.id ? '#ecfdf5' : 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontWeight: 800, margin: 0, fontSize: '0.88rem' }}>
                          Return · {flight.airline} {flight.flightNumber}
                        </p>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>
                          {flight.departure} → {flight.arrival}
                          {flight.travelDate ? ` · ${flight.travelDate}` : ''}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <p style={{ fontWeight: 800, margin: 0 }}>
                          {flight.departureTime} – {flight.arrivalTime}
                        </p>
                        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: '0.15rem 0 0' }}>Reference ${flight.price}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {selectedFlight && (
          <div
            style={{
              padding: '1.15rem 1.5rem 1.35rem',
              borderTop: '1px solid var(--border)',
              background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '0 0 1.35rem 1.35rem',
            }}
          >
            {sentBanner && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: '#ecfdf5', color: '#047857', borderRadius: '0.75rem', fontWeight: 600, fontSize: '0.88rem' }}>
                {sentBanner}
              </div>
            )}
            <p
              style={{
                fontSize: '0.72rem',
                fontWeight: 800,
                color: 'var(--text-muted)',
                marginBottom: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              4 · Price & send
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                marginBottom: '1rem',
                padding: '0.9rem 1rem',
                background: 'white',
                borderRadius: '0.85rem',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', minWidth: 0 }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Plane size={22} color="var(--accent)" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>
                    {selectedFlight.airline} {selectedFlight.flightNumber}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                    Outbound: {selectedFlight.departure} → {selectedFlight.arrival} · {selectedFlight.departureTime}–{selectedFlight.arrivalTime}
                    {selectedReturnFlight && (
                      <>
                        <br />
                        Return: {selectedReturnFlight.departure} → {selectedReturnFlight.arrival} · {selectedReturnFlight.departureTime}–
                        {selectedReturnFlight.arrivalTime}
                      </>
                    )}
                    {!selectedReturnFlight && tripType === '1' && (
                      <>
                        <br />
                        <span style={{ color: '#b45309' }}>Return not selected yet</span>
                      </>
                    )}
                    <br />
                    {tripType === '1' && selectedReturnFlight ? (
                      (() => {
                        const { outboundRef, returnRef, total } = roundTripLegReferences(
                          selectedFlight,
                          selectedReturnFlight,
                        );
                        return (
                          <>
                            Round-trip reference: <strong>${outboundRef}</strong> outbound +{' '}
                            <strong>${returnRef}</strong> return = <strong>${total}</strong> per person
                          </>
                        );
                      })()
                    ) : (
                      <>List reference ${selectedFlight.price}</>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                  Quoted price (per person{tripType === '1' ? ', round trip' : ''})
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '0.65rem',
                    marginTop: '0.4rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 800 }}>$</span>
                    <input type="number" min={1} step={1} value={pricePerPerson} onChange={(e) => setPricePerPerson(e.target.value)} style={{ width: '120px', fontWeight: 800 }} />
                  </div>
                  <button
                    type="button"
                    disabled={sending}
                    onClick={() => void handleSend('quote')}
                    style={{
                      background: sending ? '#94a3b8' : 'var(--primary)',
                      color: 'white',
                      padding: '0.75rem 1.15rem',
                      borderRadius: '0.85rem',
                      fontWeight: 800,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      border: 'none',
                      cursor: sending ? 'wait' : 'pointer',
                      boxShadow: sending ? 'none' : '0 8px 20px rgba(30, 27, 75, 0.25)',
                    }}
                  >
                    <Mail size={18} aria-hidden />
                    {sending ? 'Sending…' : 'Send quote email'}
                  </button>
                  <button
                    type="button"
                    disabled={sending}
                    onClick={() => void handleSend('oaf')}
                    style={{
                      background: sending ? '#cbd5e1' : '#1e293b',
                      color: 'white',
                      padding: '0.75rem 1.15rem',
                      borderRadius: '0.85rem',
                      fontWeight: 800,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      border: 'none',
                      cursor: sending ? 'wait' : 'pointer',
                      boxShadow: sending ? 'none' : '0 8px 20px rgba(15, 23, 42, 0.2)',
                    }}
                  >
                    <CreditCard size={18} aria-hidden />
                    {sending ? 'Sending…' : 'Send OAF (payment)'}
                  </button>
                  <button
                    type="button"
                    disabled={sending}
                    onClick={() => void handleSend('ticket')}
                    style={{
                      background: sending ? '#cbd5e1' : '#f1f5f9',
                      color: '#0f172a',
                      padding: '0.75rem 1.15rem',
                      borderRadius: '0.85rem',
                      fontWeight: 800,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      border: '2px solid #e2e8f0',
                      cursor: sending ? 'wait' : 'pointer',
                    }}
                  >
                    <Ticket size={18} aria-hidden />
                    {sending ? 'Sending…' : 'Send ticket'}
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem', maxWidth: '62ch', lineHeight: 1.5 }}>
                  {tripType === '1' && selectedReturnFlight ? (
                    (() => {
                      const { outboundRef, returnRef, total } = roundTripLegReferences(
                        selectedFlight,
                        selectedReturnFlight,
                      );
                      return (
                        <>
                          Quoted amount is per person for the full round trip. Reference total: <strong>${total}</strong> (
                          ${outboundRef} outbound + ${returnRef} return). Emails and the view-ticket link include both legs.
                        </>
                      );
                    })()
                  ) : (
                    <>
                      List reference: <strong>${selectedFlight.price}</strong> per person. Client will open <strong>booking.skyluxtravel</strong> and pay on{' '}
                      <strong>payment.skyluxtravel</strong> (on localhost use <code style={{ fontSize: '0.72rem' }}>/booking</code> and{' '}
                      <code style={{ fontSize: '0.72rem' }}>/payment</code>).
                    </>
                  )}
                </p>
                <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.45rem', maxWidth: '72ch', lineHeight: 1.45 }}>
                  <strong>Send quote email</strong> — same branded quote layout; primary button opens <strong>booking</strong> so the client enters passenger details.
                  <strong> Send OAF (payment)</strong> — same layout with a passenger table and <strong>Complete payment</strong> → payment checkout (OAF) with names pre-filled.
                  <strong> Send ticket</strong> — standard receipt layout. Fill in all passengers before sending OAF or ticket.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightQuoteModal;
